import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { CalendarIcon, Minus, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

export type EndType = 'never' | 'on_date' | 'after_occurrences';

interface EndDateSelectorProps {
  endType: EndType;
  onEndTypeChange: (type: EndType) => void;
  endDate: Date | null;
  onEndDateChange: (date: Date | null) => void;
  occurrences: number | null;
  onOccurrencesChange: (count: number) => void;
  disabled?: boolean;
}

export function EndDateSelector({
  endType,
  onEndTypeChange,
  endDate,
  onEndDateChange,
  occurrences,
  onOccurrencesChange,
  disabled = false,
}: EndDateSelectorProps) {
  const { t } = useTranslation('staff');
  const [calendarOpen, setCalendarOpen] = useState(false);
  // Local text state so the input can be cleared mid-edit; the numeric model
  // (`occurrences`) only ever holds a clamped number.
  const [occText, setOccText] = useState(occurrences != null ? String(occurrences) : '');
  useEffect(() => {
    setOccText(occurrences != null ? String(occurrences) : '');
  }, [occurrences]);

  return (
    <div className="space-y-4">
      <RadioGroup
        value={endType}
        onValueChange={(v) => onEndTypeChange(v as EndType)}
        disabled={disabled}
        className="space-y-3"
      >
        {/* Never */}
        <div className="flex items-center space-x-3">
          <RadioGroupItem value="never" id="end-never" />
          <Label htmlFor="end-never" className="font-normal cursor-pointer">
            {t('availability.ends.never')}
          </Label>
        </div>

        {/* On date */}
        <div className="flex items-start space-x-3">
          <RadioGroupItem value="on_date" id="end-on-date" className="mt-2" />
          <div className="flex-1 space-y-2">
            <Label htmlFor="end-on-date" className="font-normal cursor-pointer">
              {t('availability.ends.onDate')}
            </Label>
            {endType === 'on_date' && (
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !endDate && 'text-muted-foreground'
                    )}
                    disabled={disabled}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, 'PPP') : t('availability.dialog.pickEndDate')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate || undefined}
                    onSelect={(date) => {
                      onEndDateChange(date || null);
                      setCalendarOpen(false);
                    }}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>

        {/* After occurrences */}
        <div className="flex items-start space-x-3">
          <RadioGroupItem value="after_occurrences" id="end-after" className="mt-2" />
          <div className="flex-1 space-y-2">
            <Label htmlFor="end-after" className="font-normal cursor-pointer">
              {t('availability.ends.afterOccurrences')}
            </Label>
            {endType === 'after_occurrences' && (
              <div className="flex items-center gap-2">
                <div className="flex items-center">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onOccurrencesChange(Math.max(1, (occurrences || 1) - 1))}
                    disabled={disabled || (occurrences || 1) <= 1}
                    className="rounded-r-none border-r-0 px-3"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    value={occText}
                    onChange={(e) => {
                      setOccText(e.target.value);
                      const n = parseInt(e.target.value, 10);
                      if (!Number.isNaN(n)) onOccurrencesChange(Math.max(1, Math.min(365, n)));
                    }}
                    onBlur={() => {
                      const n = parseInt(occText, 10);
                      const clamped = Number.isNaN(n)
                        ? (occurrences ?? 1)
                        : Math.max(1, Math.min(365, n));
                      setOccText(String(clamped));
                      onOccurrencesChange(clamped);
                    }}
                    className="w-16 text-center rounded-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    disabled={disabled}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onOccurrencesChange(Math.min(365, (occurrences || 1) + 1))}
                    disabled={disabled || (occurrences || 1) >= 365}
                    className="rounded-l-none border-l-0 px-3"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <span className="text-sm text-gray-600">{t('availability.ends.occurrences')}</span>
              </div>
            )}
          </div>
        </div>
      </RadioGroup>
    </div>
  );
}
